// ---------------------------------------------------------------------------
// CodexProvider: drives the real Codex CLI via `codex exec` (through a Tauri
// command). Every method builds a prompt from the versioned templates, runs the
// CLI, and extracts + validates JSON.
//
// Templates are resolved from the prompt_template table (user-editable in the
// Prompts & Rubric screen) with the static PROMPTS constants as fallback, so
// edits made in the UI actually drive the AI.
//
// Malformed JSON is retried automatically (settings.maxRetries): the model gets
// one repair re-prompt per retry before the error surfaces to the UI.
//
// This is the only AIProvider — there is no offline/mock fallback. When the CLI
// is unavailable or returns unparseable output, the error is surfaced to the UI
// (each catch re-throws with operation context, e.g. "Codex grading failed: …")
// so you can see exactly whether — and how — Codex is working, instead of getting
// silently substituted fake output.
// ---------------------------------------------------------------------------

import type {
  DentalProgram,
  Difficulty,
  GeneratedAnswer,
  GradingResult,
  InterviewQuestion,
  InterviewType,
  MockInterviewQuestionPlan,
  ProfileInputs,
  PromptAgent,
  RedFlagPlan,
  SchoolDossier,
  ScoredAttempt,
  SchoolFitProfile,
  StructuredProfile,
} from '../types';
import type {
  AIProvider,
  GradeRequest,
  ImprovementPlan,
  ProviderContext,
  ResearchOptions,
  ResearchedQuestion,
} from './provider';
import type { AppSettings } from '../types';
import { codexExec, isTauri } from './tauri';
import { PROMPTS, fillTemplate, wrapData } from './prompts';
import {
  answerSchema,
  dossierSchema,
  extractJson,
  fitSchema,
  gradingSchema,
  profileInputsSchema,
  profileSchema,
  redFlagPlanSchema,
  researchSchema,
} from './json';
import { getPromptTemplate } from '../db/repository';

const JSON_REPAIR_NOTE =
  '\n\nIMPORTANT: your previous response could not be parsed as the requested JSON. ' +
  'Reply again with ONLY the valid JSON payload — no prose, no code fences, no explanations.';

/**
 * Stamp lastCheckedAt on every fit-source entry the model returned. The model
 * supplies sourceUrl + confidence; the check time is a fact about THIS run.
 */
function stampFitSources(fit: SchoolFitProfile): SchoolFitProfile {
  if (!fit.sources) return fit;
  const now = new Date().toISOString();
  const sources: NonNullable<SchoolFitProfile['sources']> = {};
  for (const [field, src] of Object.entries(fit.sources)) {
    sources[field] = { ...src, lastCheckedAt: src.lastCheckedAt || now };
  }
  return { ...fit, sources };
}

/**
 * Alternative execution path for the hosted web app: instead of spawning the
 * Codex CLI, the finished prompt is POSTed to the backend, which fronts Azure
 * AI Foundry (and meters credits). `agent` tags the call in the usage ledger.
 */
export type CloudRun = (agent: string, prompt: string) => Promise<string>;

export class CodexProvider implements AIProvider {
  readonly kind: 'codex' | 'cloud';

  /**
   * The agent whose template was most recently resolved — used to tag cloud
   * calls in the usage ledger. Provider methods resolve their template and run
   * sequentially within one operation, so this stays accurate per call.
   */
  private currentAgent: string = 'general';

  constructor(
    private settings: AppSettings,
    private readonly cloudRun?: CloudRun,
  ) {
    this.kind = cloudRun ? 'cloud' : 'codex';
  }

  /** Resolve the active template for an agent: DB (user-edited) first, then defaults. */
  private template(agent: PromptAgent): string {
    this.currentAgent = agent;
    try {
      const stored = getPromptTemplate(agent);
      if (stored?.template.trim()) return stored.template;
    } catch {
      // DB not initialized yet (e.g. testConnection from onboarding) — use defaults.
    }
    return PROMPTS[agent]?.template ?? '';
  }

  private async run(prompt: string, webSearch: boolean, label?: string): Promise<string> {
    try {
      return await this.runInner(prompt, webSearch, label);
    } catch (e) {
      // Fire-and-forget failure telemetry (skips expected paywall errors).
      const err = e as Error & { code?: string };
      if (err.code !== 'credits_exhausted') {
        void import('../telemetry/telemetry')
          .then((t) => t.reportClientFailure('ai_failure', `${this.currentAgent}: ${err.message}`))
          .catch(() => {});
      }
      throw e;
    }
  }

  private async runInner(prompt: string, webSearch: boolean, label?: string): Promise<string> {
    // Hosted web: all model calls route through the backend — the browser
    // never runs a CLI or holds a model key. Web search is a Codex CLI
    // capability; the cloud path runs the same prompt without live search.
    if (this.cloudRun) return this.cloudRun(this.currentAgent, prompt);

    const useWebSearch = webSearch && this.settings.enableWebSearch;
    // Web-search research is far slower than plain generation (~60–150s vs a
    // few seconds), and large question batches (up to 50) can push a single
    // research run well past 5 minutes. Floor the web-search timeout at 10
    // minutes so a low configured value can't kill Codex mid-search — which
    // yields no JSON and surfaces as "research failed". Fast, non-search calls
    // keep the user's configured timeout unchanged.
    const timeoutSeconds = useWebSearch
      ? Math.max(this.settings.timeoutSeconds, 600)
      : this.settings.timeoutSeconds;
    const res = await codexExec({
      codexPath: this.settings.codexPath,
      model: this.settings.defaultModel,
      prompt,
      workingDirectory: this.settings.workingDirectory,
      webSearch: useWebSearch,
      reasoningEffort: this.settings.reasoningEffort,
      timeoutSeconds,
      label,
    });
    if (!res.ok) throw new Error(res.stderr || `codex exec failed (code ${res.code}).`);
    return res.stdout;
  }

  /**
   * Run + extract JSON + validate, with automatic repair re-prompts for
   * malformed output (honoring settings.maxRetries). Genuine CLI failures
   * (spawn errors, timeouts) are NOT retried — only parse/validation failures,
   * which is where a re-prompt actually helps.
   */
  private async runJson<T>(
    prompt: string,
    webSearch: boolean,
    label: string | undefined,
    parse: (raw: unknown) => T,
  ): Promise<T> {
    const attempts = 1 + Math.max(0, Math.floor(this.settings.maxRetries));
    let lastError: Error | null = null;
    for (let attempt = 0; attempt < attempts; attempt++) {
      const attemptPrompt = attempt === 0 ? prompt : prompt + JSON_REPAIR_NOTE;
      const attemptLabel = attempt === 0 ? label : `${label ?? 'Working…'} (retry ${attempt})`;
      const out = await this.run(attemptPrompt, webSearch, attemptLabel);
      try {
        return parse(extractJson(out));
      } catch (e) {
        lastError = e as Error;
      }
    }
    throw lastError ?? new Error('Model output could not be parsed.');
  }

  async testConnection() {
    if (this.cloudRun) {
      // Cloud mode: verify the backend AI endpoint is reachable without
      // spending a credit on a throwaway completion.
      try {
        const { getAiUsage } = await import('./cloudRunner');
        const usage = await getAiUsage();
        return {
          ok: true,
          message: `Cloud AI is ready (${usage.tier} plan, ${usage.remaining} of ${usage.allowance} credits left this month).`,
        };
      } catch (e) {
        return { ok: false, message: `Cloud AI test failed: ${(e as Error).message}` };
      }
    }
    // Codex runs in both the desktop (Tauri) build and the web build (via the
    // Vite dev/preview bridge). An empty codexPath is fine — both fall back to
    // `codex` on PATH — so we just attempt a run and report what happens.
    const where = isTauri() ? 'desktop (Tauri) build' : 'web build via the local Vite bridge';
    const model = this.settings.defaultModel.trim() || 'the CLI default model';
    try {
      const out = await this.run('Reply with exactly: {"ok": true}', false, 'Testing Codex connection…');
      const parsed = extractJson(out) as { ok?: boolean };
      return parsed?.ok
        ? { ok: true, message: `Codex responded successfully using ${model} (${where}).` }
        : { ok: true, message: `Codex responded (${where}), but not with the expected payload. It is reachable.` };
    } catch (e) {
      return { ok: false, message: `Codex test failed (${where}): ${(e as Error).message}` };
    }
  }

  async extractProfile(inputs: ProfileInputs, _ctx: ProviderContext): Promise<StructuredProfile> {
    try {
      const prompt = fillTemplate(this.template('profile'), {
        inputs: wrapData(JSON.stringify(inputs, null, 2)),
      });
      return (await this.runJson(prompt, false, 'Structuring your profile…', (raw) =>
        profileSchema.parse(raw),
      )) as StructuredProfile;
    } catch (e) {
      throw new Error(`Codex profile extraction failed: ${(e as Error).message}`);
    }
  }

  async extractProfileInputs(applicationText: string, _ctx: ProviderContext): Promise<ProfileInputs> {
    try {
      // Primary applications are long PDFs; cap the text so the prompt stays
      // within what the CLI comfortably handles via stdin.
      const MAX_CHARS = 120_000;
      const text =
        applicationText.length > MAX_CHARS
          ? `${applicationText.slice(0, MAX_CHARS)}\n[…application truncated…]`
          : applicationText;
      const prompt = fillTemplate(this.template('intake'), { application: wrapData(text) });
      return (await this.runJson(prompt, false, 'Reading your primary application…', (raw) =>
        profileInputsSchema.parse(raw),
      )) as ProfileInputs;
    } catch (e) {
      throw new Error(`Codex application intake failed: ${(e as Error).message}`);
    }
  }

  async buildSchoolFit(programName: string, ctx: ProviderContext): Promise<SchoolFitProfile> {
    // Fit-only prompt: much faster than a full question-research run, so adding
    // a school takes seconds instead of minutes.
    try {
      const prompt = fillTemplate(this.template('fit'), {
        programName,
        webSearchNote: ctx.webSearch
          ? 'Web search is enabled — use current public information.'
          : 'Web search is disabled — rely on general knowledge and keep claims conservative.',
      });
      const fit = (await this.runJson(prompt, true, `Building school-fit profile for ${programName}…`, (raw) =>
        fitSchema.parse(raw),
      )) as SchoolFitProfile;
      return stampFitSources(fit);
    } catch (e) {
      throw new Error(`Codex school-fit lookup failed: ${(e as Error).message}`);
    }
  }

  async buildSchoolDossier(programName: string, ctx: ProviderContext): Promise<SchoolDossier> {
    try {
      const prompt = fillTemplate(this.template('dossier'), {
        programName,
        webSearchNote: ctx.webSearch
          ? 'Web search is enabled — use current public information and cite real source URLs.'
          : 'Web search is disabled — rely on general knowledge, keep claims conservative, and mark confidence accordingly.',
      });
      const parsed = await this.runJson(prompt, true, `Building the ${programName} dossier…`, (raw) =>
        dossierSchema.parse(raw),
      );
      return { ...parsed, generatedAt: new Date().toISOString() } as SchoolDossier;
    } catch (e) {
      throw new Error(`Codex dossier research failed: ${(e as Error).message}`);
    }
  }

  async buildRedFlagPlan(
    profile: StructuredProfile | null,
    redFlagsText: string,
    _ctx: ProviderContext,
  ): Promise<RedFlagPlan> {
    try {
      const prompt = fillTemplate(this.template('redflag'), {
        profile: wrapData(JSON.stringify(profile ?? {}, null, 2)),
        redFlags: wrapData(redFlagsText.trim() || '(none stated)'),
      });
      const parsed = await this.runJson(prompt, false, 'Preparing your red-flag coaching…', (raw) =>
        redFlagPlanSchema.parse(raw),
      );
      return { ...parsed, generatedAt: new Date().toISOString() };
    } catch (e) {
      throw new Error(`Codex red-flag coaching failed: ${(e as Error).message}`);
    }
  }

  async researchProgram(
    program: { name: string; fit?: SchoolFitProfile },
    options: ResearchOptions,
    ctx: ProviderContext,
  ) {
    try {
      const count = Math.max(0, options.count);
      const existing = options.existingQuestions.slice(0, 200);
      const prompt = fillTemplate(this.template('research'), {
        programName: program.name,
        count: String(count),
        existingQuestions: existing.length
          ? wrapData(existing.map((q) => `- ${q}`).join('\n'))
          : '(none yet)',
        categoryFocus: options.category
          ? `Focus EXCLUSIVELY on questions in the category "${options.category}" — do not return questions from any other category.`
          : 'No category restriction — collect questions across all categories.',
        webSearchNote: ctx.webSearch
          ? 'Web search is enabled — cite real source URLs.'
          : 'Web search is disabled — rely on general knowledge and mark confidence accordingly.',
      });
      // researchSchema requires the fit object and validates every question's
      // fields, so a malformed response triggers the JSON-repair re-prompt
      // instead of leaking bad rows into the question bank.
      const parsed = await this.runJson(prompt, true, `Researching ${program.name}…`, (raw) =>
        researchSchema.parse(raw),
      );
      const questions: ResearchedQuestion[] = parsed.questions;
      return {
        fit: stampFitSources(parsed.fit as SchoolFitProfile),
        questions: count > 0 ? questions.slice(0, count) : questions,
      };
    } catch (e) {
      throw new Error(`Codex program research failed: ${(e as Error).message}`);
    }
  }

  async generateAnswer(
    question: InterviewQuestion,
    program: DentalProgram | null,
    profile: StructuredProfile | null,
    _ctx: ProviderContext,
  ): Promise<GeneratedAnswer> {
    try {
      const prompt = fillTemplate(this.template('answer'), {
        question: question.question,
        category: question.category,
        programName: program?.name ?? 'a dental program',
        profile: JSON.stringify(profile ?? {}, null, 2),
      });
      return (await this.runJson(prompt, false, 'Drafting a model answer…', (raw) =>
        answerSchema.parse(raw),
      )) as GeneratedAnswer;
    } catch (e) {
      throw new Error(`Codex answer generation failed: ${(e as Error).message}`);
    }
  }

  async generateSimilarQuestions(
    question: InterviewQuestion,
    count: number,
    _ctx: ProviderContext,
  ): Promise<string[]> {
    try {
      const prompt =
        `Generate ${count} NEW dental school interview questions in the category "${question.category}", ` +
        `each testing the same competency as this one but from a different angle or scenario (no trivial rewordings): "${question.question}"\n` +
        `Each entry is one plain-text question ending with "?" — no numbering, no commentary.\n` +
        `Return ONLY a valid JSON array of exactly ${count} strings — no prose, no markdown code fences.`;
      return await this.runJson(prompt, false, 'Generating similar questions…', (raw) => {
        if (Array.isArray(raw)) return raw.map(String).slice(0, count);
        throw new Error('response was not a JSON array.');
      });
    } catch (e) {
      throw new Error(`Codex question generation failed: ${(e as Error).message}`);
    }
  }

  async gradeAnswer(req: GradeRequest, ctx: ProviderContext): Promise<GradingResult> {
    try {
      const prompt = fillTemplate(this.template('grading'), {
        rubricVersion: ctx.rubricVersion,
        question: req.question,
        category: req.category,
        programName: req.programName || 'a dental program',
        schoolFit: JSON.stringify(req.schoolFit ?? {}),
        profile: JSON.stringify(req.profile ?? {}),
        transcript: wrapData(req.transcript),
      });
      const parsed = await this.runJson(prompt, false, 'Grading your answer…', (raw) =>
        gradingSchema.parse(raw),
      );
      return { ...parsed, rubricVersion: ctx.rubricVersion } as GradingResult;
    } catch (e) {
      throw new Error(`Codex grading failed: ${(e as Error).message}`);
    }
  }

  async planMockInterview(params: {
    program: DentalProgram | null;
    durationMinutes: number;
    interviewType: InterviewType;
    difficulty: Difficulty;
    questionPool: InterviewQuestion[];
  }): Promise<MockInterviewQuestionPlan[]> {
    try {
      const prompt = fillTemplate(this.template('mock'), {
        durationMinutes: String(params.durationMinutes),
        interviewType: params.interviewType,
        difficulty: params.difficulty,
        programName: params.program?.name ?? 'a dental program',
        questionPool: JSON.stringify(
          params.questionPool.map((q) => ({ id: q.id, question: q.question, category: q.category })),
        ),
      });
      return await this.runJson(prompt, false, 'Planning your mock interview…', (raw) => {
        if (Array.isArray(raw) && raw.length) {
          return raw.map((p: any) => ({
            questionId: params.questionPool.find((q) => q.question === p.question)?.id ?? null,
            question: String(p.question ?? ''),
            category: p.category,
            role: String(p.role ?? ''),
            suggestedMinutes: Number(p.suggestedMinutes ?? 3),
          }));
        }
        throw new Error('response was empty or not a plan array.');
      });
    } catch (e) {
      throw new Error(`Codex mock-interview planning failed: ${(e as Error).message}`);
    }
  }

  async generateFollowUps(question: string, answer: string, _ctx: ProviderContext): Promise<string[]> {
    try {
      const prompt = fillTemplate(this.template('followup'), { question, answer: wrapData(answer) });
      return await this.runJson(prompt, false, 'Thinking of follow-up questions…', (raw) => {
        if (Array.isArray(raw)) return raw.map(String).slice(0, 3);
        throw new Error('response was not a JSON array.');
      });
    } catch (e) {
      throw new Error(`Codex follow-up generation failed: ${(e as Error).message}`);
    }
  }

  async buildImprovementPlan(attempts: ScoredAttempt[], _ctx: ProviderContext): Promise<ImprovementPlan> {
    try {
      const prompt = fillTemplate(this.template('improvement'), {
        attempts: JSON.stringify(
          attempts.slice(-20).map((a) => ({ scores: a.scores, finalScore: a.finalScore, flags: a.grading.flags })),
        ),
      });
      return await this.runJson(prompt, false, 'Building your improvement plan…', (raw) => {
        const plan = raw as ImprovementPlan;
        if (plan && plan.topWeaknesses) return plan;
        throw new Error('response was missing the expected plan fields.');
      });
    } catch (e) {
      throw new Error(`Codex improvement-plan generation failed: ${(e as Error).message}`);
    }
  }
}
